<template lang="pug">
v-container(fluid)
  // Filtros y controles
  v-card.mb-4
    v-card-title
      | Reporte de Ventas Mensuales
      v-spacer
      v-select(
        v-model="filters.year"
        :items="yearOptions"
        label="Año"
        dense
        outlined
        hide-details
        class="mr-4 mt-n2"
        style="max-width: 150px"
      )
      v-btn(
        color="primary"
        @click="exportData"
        :loading="exporting"
      )
        v-icon(left) mdi-file-excel
        | Exportar

  // Mensaje si no hay datos
  v-alert(v-if="!hasSalesData" type="info" text dense class="mt-3")
    | Selecciona un año para ver el reporte de ventas mensuales

  // KPIs principales (solo si hay datos seleccionados)
  template(v-if="hasSalesData")
    // Resumen anual
    v-row
      v-col(cols="12" sm="6" md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Ventas Totales Anuales
            .text-h4 ${{ numberFormat(annualTotals.revenue || 0) }}
      v-col(cols="12" sm="6" md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Órdenes Totales Anuales
            .text-h4 {{ annualTotals.orders || 0 }}
      v-col(cols="12" sm="6" md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Ticket Promedio Anual
            .text-h4 ${{ numberFormat((annualTotals.revenue && annualTotals.orders) ? annualTotals.revenue / annualTotals.orders : 0) }}
      v-col(cols="12" sm="6" md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Productos Vendidos Anuales
            .text-h4 {{ annualTotals.quantity || 0 }}

    // Tabla de ventas mensuales
    v-row.mt-4
      v-col(cols="12")
        v-card(outlined)
          v-card-title Ventas por Mes
          v-card-text
            v-data-table(
              :headers="monthlyHeaders"
              :items="monthlySales"
              :items-per-page="12"
              sort-by="month"
              dense
              :loading="loading"
            )
              template(v-slot:item.revenue="{ item }")
                span ${{ numberFormat(item.revenue) }}
              template(v-slot:item.ticketAvg="{ item }")
                span ${{ numberFormat(item.ticketAvg) }}

    // Gráfico de ventas mensuales
    v-row.mt-4
      v-col(cols="12")
        v-card(outlined)
          v-card-title Evolución de Ventas Mensuales
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart"
              :height="300"
            )
            bar-chart(
              v-else-if="monthlySalesChartData"
              :chart-data="monthlySalesChartData"
              :chart-options="barChartOptions"
              :height="300"
            )

    // Distribución de ventas
    v-row.mt-4
      v-col(cols="12" md="6")
        v-card(outlined)
          v-card-title Distribución por Tipo de Orden
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart"
              :height="300"
            )
            v-chart-types(
              v-else-if="sales && sales.orderTypeSummary && sales.orderTypeSummary.length > 0" 
              :order-types="sales.orderTypeSummary" 
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-3"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de tipos de orden para mostrar en este año
      v-col(cols="12" md="6")
        v-card(outlined)
          v-card-title Distribución por Medios de Pago
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart"
              :height="300"
            )
            pie-chart(
              v-else-if="paymentMethodsChartData && sales.paymentSummary && sales.paymentSummary.length > 0"
              :chart-data="paymentMethodsChartData"
              :chart-options="chartOptions"
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-3"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de medios de pago para mostrar en este año
    
    // Productos vendidos y detalles
    v-row.mt-4
      v-col(cols="12" md="5")
        v-card(outlined)
          v-card-title Detalle de Productos Vendidos
          v-card-text
            v-data-table(
              :headers="productHeaders"
              :items="sales.productSummary || []"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
              sort-by="quantity"
              sort-desc
              dense
            )
              template(v-slot:item.total="{ item }")
                span ${{ numberFormat(item.total) }}
      v-col(cols="12" md="7")
        v-card(outlined)
          v-card-title Productos Más Vendidos (Top 10)
          v-card-text.chart-container
            v-skeleton-loader(
              v-if="loading"
              type="chart" 
              :height="300"
            )
            bar-chart(
              v-else-if="productsChartData && sales.productSummary && sales.productSummary.length > 0"
              :chart-data="productsChartData"
              :chart-options="barChartOptions" 
              :height="300"
            )
            v-alert(
              v-else
              type="info"
              text
              dense
              color="blue-grey lighten-3"
              class="my-2"
            )
              v-icon(left small) mdi-information
              | No hay datos de productos para mostrar en este año

  // Modal para ver todos los productos
  v-dialog(v-model="showAllProducts" max-width="700px")
    v-card
      v-card-title Todos los Productos Vendidos
      v-card-text
        v-data-table(
          :headers="productHeaders"
          :items="sales && sales.productSummary ? sales.productSummary : []"
          :items-per-page="10"
          :footer-props="{'items-per-page-options': [10, 20, 50, -1]}"
        )
          template(v-slot:item.total="{ item }")
            | ${{ numberFormat(item.total) }}
      v-card-actions
        v-spacer
        v-btn(color="primary" text @click="showAllProducts = false") Cerrar
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ChartOrderTypes from '@/components/charts/OrderTypesChart.vue'

// Importar los componentes de gráficos
import PieChart from '@/components/charts/PieChart.vue'
import BarChart from '@/components/dashboard/BarChart.vue'

export default {
  name: 'MonthlySalesReport',
  
  components: {
    'v-chart-types': ChartOrderTypes,
    'pie-chart': PieChart,
    'bar-chart': BarChart
  },
  
  data() {
    // Obtener año actual
    const currentYear = new Date().getFullYear()
    
    return {
      filters: {
        year: currentYear.toString()
      },
      // Opciones de años (últimos 5 años)
      yearOptions: Array.from({ length: 5 }, (_, i) => (currentYear - i).toString()),
      showAllProducts: false,
      menu: false,
      // Headers para la tabla de productos
      productHeaders: [
        { text: 'Producto', value: 'name', sortable: true },
        { text: 'Cantidad', value: 'quantity', sortable: true },
        { text: 'Total', value: 'total', sortable: true }
      ],
      // Headers para la tabla mensual
      monthlyHeaders: [
        { text: 'Mes', value: 'monthName', sortable: true },
        { text: 'Ventas', value: 'revenue', sortable: true },
        { text: 'Órdenes', value: 'orders', sortable: true },
        { text: 'Ticket Promedio', value: 'ticketAvg', sortable: true },
        { text: 'Productos Vendidos', value: 'quantity', sortable: true }
      ],
      // Opciones para gráficos
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.raw || 0;
                return `${label}: $${this.numberFormat(value)}`;
              }
            }
          }
        }
      },
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => `${this.numberFormat(value)}`
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.raw || 0;
                return `${label}: ${this.numberFormat(value)}`;
              }
            }
          }
        }
      },
      // Nombres de los meses en español
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ]
    }
  },
  
  computed: {
    ...mapGetters({
      sales: 'monthlySalesReports/sales',
      monthlySales: 'monthlySalesReports/monthlySales',
      loading: 'monthlySalesReports/loading',
      exporting: 'monthlySalesReports/exporting'
    }),
    
    hasSalesData() {
      return this.sales && 
             Object.keys(this.sales).length > 0 && 
             this.monthlySales && 
             this.monthlySales.length > 0
    },
    
    // Totales anuales calculados a partir de los datos mensuales
    annualTotals() {
      if (!this.monthlySales || this.monthlySales.length === 0) {
        return { revenue: 0, orders: 0, quantity: 0 }
      }
      
      return this.monthlySales.reduce((totals, month) => {
        return {
          revenue: totals.revenue + (month.revenue || 0),
          orders: totals.orders + (month.orders || 0),
          quantity: totals.quantity + (month.quantity || 0)
        }
      }, { revenue: 0, orders: 0, quantity: 0 })
    },
    
    // Datos para el gráfico de medios de pago
    paymentMethodsChartData() {
      if (!this.sales || !this.sales.paymentSummary || this.sales.paymentSummary.length === 0) {
        return null
      }
      
      // Ordenar por monto (mayor a menor)
      const sortedPayments = [...this.sales.paymentSummary].sort((a, b) => b.amount - a.amount)
      
      return {
        labels: sortedPayments.map(item => this.getPaymentMethodName(item)),
        datasets: [
          {
            data: sortedPayments.map(item => item.amount),
            backgroundColor: [
              '#B39DDB', // Morado
              '#F48FB1', // Rosa
              '#EF9A9A', // Rojo claro
              '#9FA8DA', // Azul claro
              '#81C784', // Verde claro
              '#FFE082', // Amarillo claro
              '#BCAAA4', // Marrón claro
              '#90CAF9', // Azul celeste
              '#CE93D8', // Morado claro
              '#80CBC4'  // Verde agua
            ]
          }
        ]
      }
    },
    
    // Datos para el gráfico de productos
    productsChartData() {
      if (!this.sales || !this.sales.productSummary || this.sales.productSummary.length === 0) {
        return null
      }
      
      // Ordenar por cantidad (mayor a menor) y tomar los 10 primeros
      const topProducts = [...this.sales.productSummary]
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10)
      
      return {
        labels: topProducts.map(item => item.name),
        datasets: [
          {
            label: 'Cantidad vendida',
            data: topProducts.map(item => item.quantity),
            backgroundColor: '#B39DDB',
            borderColor: '#9575CD',
            borderWidth: 1
          }
        ]
      }
    },
    
    // Datos para el gráfico de ventas mensuales
    monthlySalesChartData() {
      if (!this.monthlySales || this.monthlySales.length === 0) {
        return null
      }
      
      // Ordenar por mes (1-12)
      const sortedMonths = [...this.monthlySales].sort((a, b) => a.month - b.month)
      
      return {
        labels: sortedMonths.map(item => item.monthName),
        datasets: [
          {
            label: 'Ventas mensuales',
            data: sortedMonths.map(item => item.revenue),
            backgroundColor: '#B39DDB',
            borderColor: '#9575CD',
            borderWidth: 1
          }
        ]
      }
    }
  },
  
  methods: {
    ...mapActions({
      showError: 'showError',
      fetchMonthlySales: 'monthlySalesReports/fetchMonthlySales',
      exportMonthlySales: 'monthlySalesReports/exportMonthlySales',
      fetchPaymentMethods: 'paymentMethods/fetch' // Corregido para usar la acción correcta
    }),
    
    numberFormat(value, decimals = 0) {
      // Formatear números como moneda sin símbolo
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value || 0)
    },
    
    getPaymentMethodName(payment) {
      // Si payment tiene methodName, usar ese nombre
      if (payment && payment.methodName) {
        return payment.methodName;
      }
      
      // Si payment tiene methodKey, usar ese methodKey
      if (payment && payment.methodKey) {
        // Nombres amigables para los métodos de pago
        const paymentNames = {
          'cash': 'Efectivo',
          'card': 'Tarjeta',
          'transfer': 'Transferencia',
          'mercadopago': 'Mercado Pago',
          'rappi': 'Rappi',
          'pedidosya': 'PedidosYa'
        }
        return paymentNames[payment.methodKey] || payment.methodKey;
      }
      
      // Nombres amigables para los métodos de pago (caso legacy)
      const paymentNames = {
        'cash': 'Efectivo',
        'card': 'Tarjeta',
        'transfer': 'Transferencia',
        'mercadopago': 'Mercado Pago',
        'rappi': 'Rappi',
        'pedidosya': 'PedidosYa'
      }
      return paymentNames[payment] || payment;
    },
    
    fetchData() {
      this.fetchMonthlySales(this.filters)
        .catch(error => {
          this.showError('Error al cargar los datos de ventas mensuales')
          console.error('Error fetching monthly sales:', error)
        })
    },
    
    exportData() {
      this.exportMonthlySales(this.filters)
        .catch(error => {
          this.showError('Error al exportar el reporte de ventas mensuales')
          console.error('Error exporting monthly sales report:', error)
        })
    },
    
    loadPaymentMethods() {
      this.fetchPaymentMethods() // Usar el nombre mapeado
        .catch(error => {
          console.error('Error loading payment methods:', error)
        })
    }
  },
  
  watch: {
    'filters.year': function() {
      this.fetchData()
    }
  },
  
  created() {
    // Cargar datos al crear el componente
    this.fetchData()
    this.loadPaymentMethods()
  }
}
</script>

<style scoped>
.v-card__text {
  position: relative;
}

.chart-container {
  position: relative;
  height: 300px;
}

.text-h4 {
  font-weight: 500;
}

.text-overline {
  color: rgba(0, 0, 0, 0.6);
}
</style>
